<script>
import bem from '@/utils/bem';
import { mapActions, mapGetters, mapState } from 'vuex';
import NewFrameOption from '@/components/modals/New-frame-option.vue';
import ChangeProduct from '@/components/modals/Change-product.vue';

const b = bem('template-frame-options');

export default {
  components: {
    NewFrameOption,
    ChangeProduct,
  },
  data() {
    return {
      expandedSizes: [],
      orientationOptions: [
        { value: 'vertical', label: 'Vertical' },
        { value: 'horizontal', label: 'Horizontal' },
      ],
      colors: [],
      show: false,
      showBulkEdit: false,
      selectedOptionForBulk: null,
    };
  },
  props: {
    selectedCollection: {
      type: Object,
      required: true,
    },
    toggleOptionDisplays: {
      type: Function,
      required: true,
    },
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    updateOptionField(option) {
      const value = {
        type: option.name,
        productId: option.parentId,
        optionId: option.id,
        option: {
          displayName: option.name,
          price: option.price,
          additionalDiscount: option.additionalDiscount,
          inStock: option.inStock,
          isDefaultOption: option.isDefaultOption,
          isDefaultMinOption: option.isDefaultMinOption,
          isPersonalize: option.isPersonalize,
          frameColorId: option.frameColor.id || option.frameColor,
          frameSizeId: option.frameSize.id || option.frameSize,
          frameOrientation: option.frameOrientation,
          isTaxApplied: true,
          personalizeImage: {
            imageKey: '',
            imageUri: '',
          },
        },
      };
      this.updateFrameOptionAction(value);
    },
    ...mapActions({
      updateFrameOptionAction: 'createTemplate/updateFrameOption',
      deleteFrameOptionAction: 'createTemplate/deleteFrameOption',
      getCollagePreviewImagesAction: 'createTemplate/getCollagePreviewImages',
    }),
    toggleShow() {
      this.show = true;
    },
    onDelete(option) {
      this.deleteFrameOptionAction({
        optionId: option.id,
        frameId: option.parentId,
        type: option.name,
      });
    },
    showBulkEditModal(option) {
      this.selectedOptionForBulk = option;
      this.showBulkEdit = true;
    },
  },
  computed: {
    ...mapGetters({
      frameOptions: 'createTemplate/frameOptions',
      frameSizes: 'createTemplate/frameSizes',
      frameColorsGetter: 'createTemplate/frameColors',
    }),
    ...mapState({
      frameDetails: (state) => state.createTemplate.frameDetails,
      frameColors: (state) => state.createTemplate.colors,
    }),
  },
  mounted() {
    this.colors = this.frameColorsGetter(this.selectedCollection.id);
    if (this.frameDetails) {
      this.getCollagePreviewImagesAction(this.frameDetails.id);
    }
  },
  watch: {
    frameDetails: {
      handler(newValue) {
        if (newValue) {
          this.getCollagePreviewImagesAction(newValue.id);
        }
      },
      deep: true,
    },
    selectedCollection: {
      handler(newValue) {
        if (newValue) {
          this.colors = this.frameColorsGetter(newValue.id);
        }
      },
      deep: true,
    },
    frameColors: {
      handler(newValue) {
        if (newValue) {
          this.colors = this.frameColorsGetter(this.selectedCollection.id);
        }
      },
      deep: true,
    },
  },
};
</script>

<template>
  <div :class="b()">
    <a-collapse
      v-model:activeKey="expandedSizes"
      :bordered="false"
      expand-icon-position="end"
      :class="b('collapse')"
    >
      <a-collapse-panel
        v-for="sizeGroup in frameOptions"
        :key="sizeGroup.name"
        :class="b('size-panel')"
      >
        <template #header>
          <div :class="b('size-header')">
            <span :class="b('size-name')">{{ sizeGroup.name }}</span>
            <span :class="b('size-count')">{{ sizeGroup.options.length }} variants</span>
          </div>
        </template>

        <div :class="b('cards-grid')">
          <div
            v-for="option in sizeGroup.options"
            :key="option.id"
            :class="b('card')"
          >
            <!-- Card Header: Color + Orientation -->
            <div :class="b('card-header')">
              <a-select
                v-model:value="option.frameColor.id"
                :options="colors"
                size="small"
                :class="b('card-select')"
                placeholder="Color"
                @change="updateOptionField(option)"
              />
              <a-select
                v-model:value="option.frameOrientation"
                :options="orientationOptions"
                size="small"
                :class="b('card-select')"
                placeholder="Orientation"
                @change="updateOptionField(option)"
              />
            </div>

            <!-- Card Body: Price + Discount -->
            <div :class="b('card-body')">
              <div :class="b('card-field')">
                <span :class="b('card-label')">Price</span>
                <a-input
                  v-model:value="option.price"
                  type="number"
                  size="small"
                  @change="updateOptionField(option)"
                />
              </div>
              <div :class="b('card-field')">
                <span :class="b('card-label')">Discount</span>
                <a-input
                  v-model:value="option.additionalDiscount"
                  type="number"
                  size="small"
                  @change="updateOptionField(option)"
                />
              </div>
            </div>

            <!-- Card Toggles: 2x2 grid -->
            <div :class="b('card-toggles')">
              <div :class="b('card-toggle')">
                <span>Stock</span>
                <a-switch
                  v-model:checked="option.inStock"
                  size="small"
                  @change="updateOptionField(option)"
                />
              </div>
              <div :class="b('card-toggle')">
                <span>Default</span>
                <a-switch
                  v-model:checked="option.isDefaultOption"
                  size="small"
                  @change="updateOptionField(option)"
                />
              </div>
              <div :class="b('card-toggle')">
                <span>Personalize</span>
                <a-switch
                  v-model:checked="option.isPersonalize"
                  size="small"
                  @change="updateOptionField(option)"
                />
              </div>
              <div :class="b('card-toggle')">
                <span>Min Def</span>
                <a-switch
                  v-model:checked="option.isDefaultMinOption"
                  size="small"
                  @change="updateOptionField(option)"
                />
              </div>
            </div>

            <!-- Card Actions -->
            <div :class="b('card-actions')">
              <a-button size="small" @click="toggleOptionDisplays(false, option)">
                Displays
              </a-button>
              <a-button size="small" @click="showBulkEditModal(option)">
                Edit bulk
              </a-button>
              <a-popconfirm title="Sure to delete?" @confirm="onDelete(option)">
                <a-button size="small" danger>Delete</a-button>
              </a-popconfirm>
            </div>
          </div>
        </div>
      </a-collapse-panel>
    </a-collapse>

    <a-button type="primary" :class="b('add-btn')" @click="toggleShow">
      + Add new option
    </a-button>

    <div v-if="show">
      <NewFrameOption
        v-model:show="show"
        :frame-colors="colors"
        :frame-sizes="frameSizes"
        :show="show"
      />
    </div>
    <div v-if="showBulkEdit && selectedOptionForBulk">
      <ChangeProduct
        v-model:show="showBulkEdit"
        :isShow="showBulkEdit"
        :product="selectedOptionForBulk"
        :full-product="frameDetails"
        :isNewProduct="false"
        :hideFields="true"
        button-text="Edit"
        title="Edit bulk discount"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/variables';

.template-frame-options {
  &__collapse {
    background: transparent;

    :deep(.ant-collapse-item) {
      border: 1px solid rgba($horizon, 0.1);
      border-radius: 8px;
      margin-bottom: 12px;
      overflow: hidden;
    }

    :deep(.ant-collapse-header) {
      padding: 16px 20px !important;
      background: rgba($horizon, 0.02);
    }

    :deep(.ant-collapse-content-box) {
      padding: 16px !important;
    }
  }

  &__size-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding-right: 24px;
  }

  &__size-name {
    font-size: 16px;
    font-weight: 600;
    color: $ebony-clay-gray-color;
  }

  &__size-count {
    font-size: 13px;
    color: rgba($ebony-clay-gray-color, 0.6);
  }

  &__cards-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
  }

  &__card {
    background: $white-color;
    border: 1px solid rgba($horizon, 0.1);
    border-radius: 8px;
    padding: 16px;
    transition: box-shadow 0.2s ease;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }
  }

  &__card-header {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
  }

  &__card-select {
    flex: 1;
  }

  &__card-body {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 12px;
  }

  &__card-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__card-label {
    font-size: 12px;
    color: rgba($ebony-clay-gray-color, 0.6);
  }

  &__card-toggles {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-bottom: 12px;
    padding: 12px;
    background: rgba($horizon, 0.04);
    border-radius: 6px;
  }

  &__card-toggle {
    display: flex;
    justify-content: space-between;
    align-items: center;

    span {
      font-size: 12px;
      color: rgba($ebony-clay-gray-color, 0.8);
    }
  }

  &__card-actions {
    display: flex;
    gap: 8px;
    padding-top: 12px;
    border-top: 1px solid rgba($horizon, 0.1);

    .ant-btn {
      flex: 1;
      font-size: 12px;
    }
  }

  &__add-btn {
    margin-top: 16px;
    width: 100%;
  }
}
</style>
